// app/javascript/controllers/crud/form_controller.js
import { Controller } from "@hotwired/stimulus"

// data-controller="crud--form"
export default class extends Controller {
  static targets = ["alert", "alertMessage", "alertList"]
  static values = {
    ajax: Boolean,
    redirect: String,
    method: String,
  }

  connect() {
    // Accordion click (delegado)
    this.element.addEventListener("click", (ev) => {
      const trigger = ev.target.closest?.('[data-accordion-trigger="true"]')
      if (!trigger) return

      ev.preventDefault()

      const targetSel = trigger.getAttribute("data-target")
      if (!targetSel) return

      const panel = this.element.querySelector(targetSel)
      if (!panel) return

      const icon = trigger.querySelector('[data-accordion-icon="true"]')
      const isOpen = !panel.classList.contains("hidden")

      if (isOpen) {
        panel.classList.add("hidden")
        trigger.setAttribute("aria-expanded", "false")
        if (icon) icon.style.transform = ""
      } else {
        panel.classList.remove("hidden")
        trigger.setAttribute("aria-expanded", "true")
        if (icon) icon.style.transform = "rotate(180deg)"
      }
    })

    // ✅ inicializa estado dos campos condicionais
    this.syncConditionalFields()

    // Submit
    this.element.addEventListener("submit", (ev) => {
      if (!this.ajaxValue) return
      if (ev.target !== this.element) return

      ev.preventDefault()
      this.hideAlert()

      // garante que required/disabled esteja correto antes de validar
      this.syncConditionalFields()

      const missing = this.validateRequiredFields()
      const dateErrors = this.validateAdmissionDate()
      const allErrors = missing.concat(dateErrors)

      if (allErrors.length > 0) {
        this.showAlert("Preencha os campos obrigatórios abaixo antes de salvar:", allErrors)
        return
      }

      // verifica bloqueio de salario (definido pelo modulo cargo_salario)
      const $jForm = typeof window.$j === "function" ? window.$j(this.element) : null
      if ($jForm && $jForm.data("salario-bloqueado")) {
        const $alerta = $jForm.find("#salario-alerta")
        if ($alerta.length) {
          $alerta[0].scrollIntoView({ behavior: "smooth", block: "center" })
        }
        return
      }

      this.submitAjax()
    })

    // Limpa marcação de inválido conforme usuário corrige
    this.element.addEventListener("input", (ev) => this.clearIfValid(ev.target))
    this.element.addEventListener("change", (ev) => {
      this.clearIfValid(ev.target)
      this.syncConditionalFields()
    })
  }

  csrfToken() {
    // 1) hidden input do form (token específico do form / per-form)
    const hidden = this.element.querySelector('input[name="authenticity_token"]')?.value
    if (hidden) return hidden

    // 2) meta (fallback)
    const meta = document.querySelector('meta[name="csrf-token"]')?.content
    if (meta) return meta

    return null
  }

  /**
   * Garante que o submit use a MESMA origem do browser.
   * Motivo: em dev é comum abrir a página em um host (localhost/127/hostname)
   * e o form.action virar absoluto apontando para outro host — aí o cookie não vai
   * e o Rails acusa InvalidAuthenticityToken (422).
   */
  normalizedSameOriginActionUrl() {
    const rawAction = this.element.getAttribute("action") || this.element.action
    try {
      const u = new URL(rawAction, window.location.href)
      // força a mesma origem do browser
      u.protocol = window.location.protocol
      u.host = window.location.host
      return u.toString()
    } catch (_e) {
      // se vier relativo/inválido, segue como está
      return rawAction
    }
  }

  // -------------------------
  // Conditional required/enabled
  // -------------------------
  syncConditionalFields() {
    // ✅ trava anti-recursão (evita loop quando disableField dispara eventos)
    if (this._syncingConditionalFields) return
    this._syncingConditionalFields = true

    try {
      // --- Checkbox-based conditionals ---
      const fields = Array.from(
        this.element.querySelectorAll("[data-required-if-checked], [data-required-if-checked-name]")
      )

      fields.forEach((field) => {
        const checkbox = this.resolveCheckboxFor(field)
        if (!checkbox) return

        const shouldRequire = checkbox.checked === true
        const enableIfChecked = this.truthy(field.dataset.enableIfChecked)

        if (enableIfChecked) {
          if (shouldRequire) this.enableField(field)
          else this.disableField(field, { clearValue: true })
        }

        if (shouldRequire) {
          this.setRequired(field, true)
        } else {
          this.setRequired(field, false)
          if (field.classList.contains("crud-invalid")) this.clearInvalid(field)
        }
      })

      // --- Radio-based conditionals ---
      const radioFields = Array.from(
        this.element.querySelectorAll("[data-required-if-radio-name]")
      )

      radioFields.forEach((field) => {
        const radioName = (field.dataset.requiredIfRadioName || "").trim()
        const radioValue = (field.dataset.requiredIfRadioValue || "").trim()
        if (!radioName) return

        const checkedRadio = this.element.querySelector(
          `input[type="radio"][name="${CSS.escape(radioName)}"]:checked`
        )
        const shouldRequire = checkedRadio != null && checkedRadio.value === radioValue
        const enableIfChecked = this.truthy(field.dataset.enableIfChecked)

        if (enableIfChecked) {
          if (shouldRequire) this.enableField(field)
          else this.disableField(field, { clearValue: true })
        }

        if (shouldRequire) {
          this.setRequired(field, true)
        } else {
          this.setRequired(field, false)
          if (field.classList.contains("crud-invalid")) this.clearInvalid(field)
        }
      })
    } finally {
      this._syncingConditionalFields = false
    }
  }

  resolveCheckboxFor(field) {
    const sel = (field.dataset.requiredIfChecked || "").trim()
    if (sel) {
      try {
        const el = this.element.querySelector(sel) || document.querySelector(sel)
        if (el && el.matches?.('input[type="checkbox"]')) return el
      } catch (_e) { }
    }

    const name = (field.dataset.requiredIfCheckedName || "").trim()
    if (name) {
      const el = this.element.querySelector(`input[type="checkbox"][name="${CSS.escape(name)}"]`)
      if (el) return el
    }

    return null
  }

  truthy(v) {
    if (v === true) return true
    const s = (v || "").toString().trim().toLowerCase()
    return s === "true" || s === "1" || s === "yes" || s === "on"
  }

  setRequired(field, required) {
    if (!(field instanceof HTMLElement)) return
    if (required) {
      field.setAttribute("required", "required")
      field.setAttribute("aria-required", "true")
    } else {
      field.removeAttribute("required")
      field.removeAttribute("aria-required")
    }
  }

  enableField(field) {
    if (!(field instanceof HTMLElement)) return
    field.disabled = false
    field.removeAttribute("readonly")
  }

  disableField(field, { clearValue = false } = {}) {
    if (!(field instanceof HTMLElement)) return

    field.disabled = true
    field.setAttribute("readonly", "readonly")
    this.setRequired(field, false)

    if (clearValue && "value" in field) {
      field.value = ""

      // opcional (seguro): evento custom sem conflitar com listeners de change/input
      try {
        field.dispatchEvent(new CustomEvent("crud:field-cleared", { bubbles: true }))
      } catch (_e) { }
    }
  }

  // -------------------------
  // Alert
  // -------------------------
  showAlert(message, items) {
    if (this.hasAlertMessageTarget) this.alertMessageTarget.innerHTML = message || ""
    if (this.hasAlertListTarget) {
      this.alertListTarget.innerHTML = (items || []).map((t) => `<li>${this.escapeHtml(t)}</li>`).join("")
    }
    if (this.hasAlertTarget) {
      this.alertTarget.classList.remove("hidden")
      this.alertTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  hideAlert() {
    if (this.hasAlertTarget) this.alertTarget.classList.add("hidden")
    if (this.hasAlertMessageTarget) this.alertMessageTarget.innerHTML = ""
    if (this.hasAlertListTarget) this.alertListTarget.innerHTML = ""
  }

  // -------------------------
  // Validation
  // -------------------------
  validateRequiredFields() {
    const missing = []
    let firstInvalid = null

    this.element.querySelectorAll(".crud-invalid").forEach((el) => this.clearInvalid(el))

    // ✅ pega required via atributo e via wrapper .required (SimpleForm)
    const required = Array.from(new Set([
      ...this.element.querySelectorAll('input[required], select[required], textarea[required], [aria-required="true"]'),
      ...this.element.querySelectorAll('.required input, .required select, .required textarea'),
    ])).filter((el) => this.isVisibleAndEnabled(el))

    for (const el of required) {
      const type = (el.getAttribute("type") || "").toLowerCase()

      // ✅ não valida hidden
      if (type === "hidden") continue

      if (type === "radio") {
        const name = el.getAttribute("name")
        if (!name) continue

        const group = Array.from(this.element.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`))
          .filter((r) => this.isVisibleAndEnabled(r))

        if (group.length && !group.some((r) => r.checked)) {
          const label = this.fieldLabel(group[0])
          missing.push(label)
          this.markInvalid(group[0])
          firstInvalid ||= group[0]
        }
        continue
      }

      if (type === "checkbox") {
        if (!el.checked) {
          const label = this.fieldLabel(el)
          missing.push(label)
          this.markInvalid(el)
          firstInvalid ||= el
        }
        continue
      }

      if (type === "file") {
        if (!el.value) {
          const label = this.fieldLabel(el)
          missing.push(label)
          this.markInvalid(el)
          firstInvalid ||= el
        }
        continue
      }

      // ✅ máscaras: money/percent "zero" deve contar como vazio quando required
      if (this.isMaskedZero(el)) {
        const label = this.fieldLabel(el)
        missing.push(label)
        this.markInvalid(el)
        firstInvalid ||= el
        continue
      }

      const val = (el.value || "").toString().trim()
      if (!val) {
        const label = this.fieldLabel(el)
        missing.push(label)
        this.markInvalid(el)
        firstInvalid ||= el
      }
    }

    const uniq = missing.filter((v, i, a) => a.indexOf(v) === i)

    // Abre todos os accordions que contenham campos inválidos
    this.element.querySelectorAll(".crud-invalid").forEach((el) => {
      const panel = el.closest?.('[data-crud--accordion-target="panel"]')
      if (panel && panel.classList.contains("hidden")) {
        const accordionWrapper = panel.closest('[data-controller="crud--accordion"]')
        if (accordionWrapper) {
          const headerBtn = accordionWrapper.querySelector('[data-crud--accordion-target="header"]')
          if (headerBtn) headerBtn.click()
        }
      }
    })

    if (firstInvalid) this.revealAndFocus(firstInvalid)
    return uniq
  }

  // Validacao especifica: data de admissao deve ser > data atual + 2 dias
  validateAdmissionDate() {
    const errors = []
    const input = this.element.querySelector('[name="params[data_admissao]"]')
    if (!input) return errors

    const raw = (input.value || "").trim()
    if (!raw) return errors // campo vazio ja e capturado pelo validateRequiredFields

    // aceita dd/mm/yyyy ou yyyy-mm-dd
    let dateObj = null
    const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (brMatch) {
      dateObj = new Date(parseInt(brMatch[3]), parseInt(brMatch[2]) - 1, parseInt(brMatch[1]))
    } else {
      const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (isoMatch) {
        dateObj = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]))
      }
    }

    if (!dateObj || isNaN(dateObj.getTime())) return errors

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const minDate = new Date(today)
    minDate.setDate(minDate.getDate() + 2)

    if (dateObj <= minDate) {
      errors.push("Data de admissão deve ser superior a 2 dias da data corrente")
      this.markInvalid(input)
    }

    return errors
  }

  readMaskKind(el) {
    try {
      const ds = el?.dataset || {}
      const byDataset = (ds.crudMaskKindValue || ds.crudMaskKind || "").toString().trim()
      if (byDataset) return byDataset.toLowerCase()
    } catch (_e) { }

    const byAttr = (el.getAttribute?.("data-crud--mask-kind-value") || "").toString().trim()
    return byAttr.toLowerCase()
  }

  isMaskedZero(el) {
    if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false
    if (el.disabled) return false

    const kind = this.readMaskKind(el)
    if (kind !== "money" && kind !== "percent") return false

    const raw = (el.value || "").toString()
    const digits = raw.replace(/\D/g, "")

    if (!digits) return true
    if (/^0+$/.test(digits)) return true

    if (kind === "percent") {
      const n = parseInt(digits, 10)
      if (!isFinite(n) || n === 0) return true
    }

    return false
  }

  revealAndFocus(el) {
    // Procura o painel do accordion que contém o campo
    const panel = el.closest?.('[data-crud--accordion-target="panel"]')
    if (panel && panel.classList.contains("hidden")) {
      // O wrapper do accordion Stimulus é o pai que tem data-controller="crud--accordion"
      const accordionWrapper = panel.closest('[data-controller="crud--accordion"]')
      if (accordionWrapper) {
        // Clica no header button para abrir via Stimulus
        const headerBtn = accordionWrapper.querySelector('[data-crud--accordion-target="header"]')
        if (headerBtn) headerBtn.click()
      }
    }

    setTimeout(() => {
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
        el.focus()
      } catch (_e) { }
    }, 100)
  }

  isVisibleAndEnabled(el) {
    if (!el) return false
    if (el.disabled) return false

    // checa se algum ancestral esta oculto (display:none ou classe .hidden)
    // ignorando paineis de accordion (que ficam .hidden mas devem ser validados)
    const isAncestorHidden = (node) => {
      let parent = node.parentElement
      while (parent && parent !== this.element) {
        // pula paineis de accordion (devem ser validados mesmo com .hidden)
        if (parent.hasAttribute("data-crud--accordion-target")) {
          parent = parent.parentElement
          continue
        }
        // display:none (jQuery .hide() ou style inline)
        if (parent.style && parent.style.display === "none") return true
        // classe .hidden do Tailwind
        if (parent.classList && parent.classList.contains("hidden")) return true
        parent = parent.parentElement
      }
      return false
    }

    if (isAncestorHidden(el)) return false

    // file inputs sao sempre visualmente ocultos (class="hidden"),
    // mas devem ser validados se o container pai estiver visivel
    const type = (el.getAttribute("type") || "").toLowerCase()
    if (type === "file") {
      // ja passou pelo check de ancestral oculto acima, entao esta visivel
      return true
    }

    // campos dentro de accordion colapsado devem ser validados
    const hiddenParent = el.closest(".hidden")
    if (hiddenParent) {
      const isAccordionPanel = hiddenParent.hasAttribute("data-crud--accordion-target")
      if (!isAccordionPanel) return false
    }

    const rects = el.getClientRects()
    if ((!rects || rects.length === 0) && !el.closest("[data-crud--accordion-target='panel']")) return false
    return true
  }

  fieldLabel(el) {
    const id = el.getAttribute("id")
    if (id) {
      const lbl = this.element.querySelector(`label[for="${CSS.escape(id)}"]`)
      if (lbl) {
        const txt = (lbl.textContent || "").replace(/\*/g, "").trim()
        if (txt) return txt
      }
    }
    const ph = (el.getAttribute("placeholder") || "").trim()
    if (ph) return ph
    const name = (el.getAttribute("name") || "").trim()
    return name || "Campo obrigatório"
  }

  markInvalid(el) {
    el.classList.add("crud-invalid")
    const id = el.getAttribute("id")
    if (id) {
      const lbl = this.element.querySelector(`label[for="${CSS.escape(id)}"]`)
      if (lbl) lbl.classList.add("crud-invalid-label")
    }

    // para file inputs ocultos, propagar borda vermelha para o container visivel
    const type = (el.getAttribute("type") || "").toLowerCase()
    if (type === "file") {
      const wrapper = el.closest(".mb-4")
      if (wrapper) {
        const box = wrapper.querySelector(".rounded-lg, .rounded-md")
        if (box) box.classList.add("crud-invalid-upload")
        const label = wrapper.querySelector("label")
        if (label) label.classList.add("crud-invalid-label")
      }
    }
  }

  clearInvalid(el) {
    el.classList.remove("crud-invalid")
    const id = el.getAttribute?.("id")
    if (id) {
      const lbl = this.element.querySelector(`label[for="${CSS.escape(id)}"]`)
      if (lbl) lbl.classList.remove("crud-invalid-label")
    }

    // limpar borda vermelha do container de upload
    const type = (el.getAttribute?.("type") || "").toLowerCase()
    if (type === "file") {
      const wrapper = el.closest(".mb-4")
      if (wrapper) {
        const box = wrapper.querySelector(".crud-invalid-upload")
        if (box) box.classList.remove("crud-invalid-upload")
        const label = wrapper.querySelector(".crud-invalid-label")
        if (label) label.classList.remove("crud-invalid-label")
      }
    }
  }

  clearIfValid(el) {
    if (!el || !(el instanceof HTMLElement)) return
    if (!el.classList.contains("crud-invalid")) return

    const type = (el.getAttribute("type") || "").toLowerCase()
    if (type === "hidden") return

    if (type === "radio") {
      const name = el.getAttribute("name")
      if (!name) return
      const checked = this.element.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`)
      if (checked) {
        const first = this.element.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]`)
        if (first) this.clearInvalid(first)
      }
      return
    }

    if (type === "checkbox") {
      if (el.checked) this.clearInvalid(el)
      return
    }

    if (type === "file") {
      if (el.value) this.clearInvalid(el)
      return
    }

    if (this.isMaskedZero(el)) return

    const val = (el.value || "").toString().trim()
    if (val) this.clearInvalid(el)
  }

  // -------------------------
  // Submit AJAX
  // -------------------------
  async submitAjax() {
    try {
      const token = this.csrfToken()

      // normaliza datepicker BR -> YYYY-MM-DD
      this.element.querySelectorAll('input[data-br-datepicker="true"]').forEach((inp) => {
        const v = (inp.value || "").trim()
        const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if (m) inp.value = `${m[3]}-${m[2]}-${m[1]}`
      })

      const formData = new FormData(this.element)

      const rawAction = this.element.getAttribute("action") || this.element.action
      const action = this.normalizedSameOriginActionUrl()
      const method = (this.methodValue || "POST").toUpperCase()

      const headers = {
        "X-Requested-With": "XMLHttpRequest",
        "Accept": "application/json",
      }

      // CSRF
      if (token) headers["X-CSRF-Token"] = token

      // (Opcional, ajuda debug no log sem quebrar nada)
      headers["X-CRUD-Page-Origin"] = window.location.origin
      headers["X-CRUD-Action-Raw"] = rawAction

      const res = await fetch(action, {
        method,
        headers,
        body: formData,
        credentials: "include", // <-- sempre enviar cookies (robusto para dev e prod)
      })

      if (res.ok) {
        if (this.redirectValue) window.location.replace(this.redirectValue)
        else window.location.reload()
        return
      }

      // tenta ler JSON (fluxo padrão)
      let payload = null
      try { payload = await res.json() } catch (_e) { }

      const erros = payload?.erros || []
      const msg = payload?.mensagem || "Falha ao salvar. Verifique os campos e tente novamente."
      this.showAlert(msg, erros)

    } catch (e) {
      console.error("[CRUD][form] submitAjax error:", e)
      this.showAlert("Falha ao salvar. Verifique os campos e tente novamente.", [])
    }
  }

  escapeHtml(s) {
    return (s || "").toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }
}
